import React from 'react';
import {
  Square,
  Heading,
  Stack,
  Flex,
  Loader,
} from '@grubhubprod/gh-cookbook-react/dist';
import ExemptionCertificate from 'src/components/enterprise/features/TaxExemptions/ExemptionCertificateList/ExemptionCertificate';
import Table from 'src/components/Table/Table';
import ExemptionCertificatesTableHeader from 'src/components/enterprise/features/TaxExemptions/ExemptionCertificateList/ExemptionCertificatesTableHeader';
import NoCertificatesMessage from 'src/components/enterprise/features/TaxExemptions/ExemptionCertificateList/NoCertificatesMessage';
import {
  useSortTaxExemptionCertificates,
  usePaginatedTaxExemptionCertificates,
} from 'src/components/enterprise/features/TaxExemptions/hooks';
import { TaxExemptionCertificate } from 'src/types/TaxExemptionCertificate';
import { defaultSortConfig } from 'src/components/enterprise/features/TaxExemptions/ExemptionCertificateList/sortConfiguration';
import { Pagination } from 'src/components/Pagination/Pagination';
import { PAGE_SIZE } from 'src/utilities/useSearchHooks';
import PageSection from 'src/components/enterprise/layout/PageSection';

export type ExemptionsCertificateListProps = {
  certificates: TaxExemptionCertificate[];
  isLoading: boolean;
};

export default function ExemptionCertificateList({
  certificates,
  isLoading,
}: ExemptionsCertificateListProps): JSX.Element {
  const { sortBy, sortConfig, sortedCertificates } =
    useSortTaxExemptionCertificates(certificates, defaultSortConfig);
  const { currentPage, currentPageCertificates, setCurrentPage } =
    usePaginatedTaxExemptionCertificates(sortedCertificates, PAGE_SIZE);

  const showEmptyMessage = !isLoading && certificates.length === 0;
  const showCertificatesList = certificates.length > 0;
  const showPagination = sortedCertificates.length / PAGE_SIZE > 1;

  const renderCertificates = () => {
    return currentPageCertificates.map((certificate) => (
      <ExemptionCertificate
        key={certificate.certificateId}
        certificate={certificate}
      />
    ));
  };

  return (
    <PageSection>
      {isLoading && (
        <Flex
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="40vh"
          data-testid="loader"
        >
          <Loader size="md" />
        </Flex>
      )}
      {showEmptyMessage && <NoCertificatesMessage />}
      {showCertificatesList && (
        <Square>
          <Heading variant="h4">{`Exemption certificates (${certificates.length})`}</Heading>
          <Stack y={showPagination ? 'x-lg' : undefined}>
            <Table
              header={() => (
                <ExemptionCertificatesTableHeader
                  sortBy={sortBy}
                  sortConfig={sortConfig}
                />
              )}
              body={() => renderCertificates()}
              showFooter={false}
              hideOutsideBorder
            />
          </Stack>
        </Square>
      )}
      {showPagination && (
        <Flex justifyContent="center">
          <Pagination
            boundaryLinks
            page={currentPage}
            pageSize={PAGE_SIZE}
            collectionSize={sortedCertificates.length}
            pageChange={setCurrentPage}
          />
        </Flex>
      )}
    </PageSection>
  );
}
